import { useAnimatedDots } from '@hooks/useAnimatedDots';
import { ActionButton, ActionButtonProps } from '@pages/ActionButton';
import { forwardRef } from 'react';
import { LoadingStatusPill } from './LoadingStatusPill';

export interface ActionButtonWithLoadingProps extends ActionButtonProps {
  isLoading?: boolean;
  progress?: number; // 0-100, optional progress percentage
}

export const ActionButtonWithLoading = forwardRef<HTMLButtonElement, ActionButtonWithLoadingProps>(
  (
    { isLoading = false, progress, variant = 'primary', className, padding, ...buttonProps },
    ref,
  ) => {
    const visibleDotCount = useAnimatedDots(isLoading);

    // Transform button text: "Add" -> "Adding" when loading, and add animated dots
    const getLoadingText = (text: React.ReactNode): React.ReactNode => {
      if (!isLoading) return text;

      if (typeof text === 'string') {
        // Replace "Add" with "Adding" at the start of words
        const addingText = text.replace(/\bAdd\b/g, 'Adding');
        // Add animated dots
        return (
          <>
            {addingText}
            <span style={{ visibility: visibleDotCount >= 1 ? 'visible' : 'hidden' }}>.</span>
            <span style={{ visibility: visibleDotCount >= 2 ? 'visible' : 'hidden' }}>.</span>
            <span style={{ visibility: visibleDotCount >= 3 ? 'visible' : 'hidden' }}>.</span>
          </>
        );
      }
      return text;
    };

    // When loading, replace button with LoadingStatusPill
    if (isLoading) {
      return (
        <LoadingStatusPill progress={progress} padding={padding}>
          {getLoadingText(buttonProps.children)}
        </LoadingStatusPill>
      );
    }

    // When not loading, render ActionButton
    return (
      <ActionButton
        ref={ref}
        variant={variant}
        className={className}
        disabled={buttonProps.disabled}
        padding={padding}
        {...buttonProps}
      >
        {buttonProps.children}
      </ActionButton>
    );
  },
);

ActionButtonWithLoading.displayName = 'ActionButtonWithLoading';
